/**
 * The Settings nav's labels, in order — nothing else.
 *
 * The sibling of `components/navigation/Sidebar/mainNavOrder.ts`, and it exists for the
 * same reason plus one sharper one.
 *
 * ── Why this file, and not `import { PERSONAL_MENU } from './SettingsPage'` ─────────
 * The docs index orders its categories by the sidebar order and then this one. Reading it
 * straight off `SettingsPage.tsx` looked free — both modules are eagerly imported anyway,
 * so it adds no bundle weight. But it adds a module-graph EDGE, and that edge blanked the
 * entire app:
 *
 *   docs/index.ts → docs/categoryOrder.ts → SettingsPage.tsx → hooks barrel → … → back
 *
 * `TextSizeCard.tsx` evaluates `TEXT_ZOOM_LEVELS.map(...)` at module top level, so once
 * that cycle existed the constant was still in its temporal dead zone when the card was
 * evaluated. The app died at import with `Cannot access 'TEXT_ZOOM_LEVELS' before
 * initialization` — a blank screen, no React error boundary, nothing in the console until
 * you import `App.tsx` by hand and read the rejection.
 *
 * `PERSONAL_MENU` and `COMPANY_MENU` cannot move here wholesale: they carry JSX
 * (`component: <ProfilePage />`, `icon: <UserIcon />`), which is exactly the weight that
 * caused the cycle. Only the ORDER is shared, and order is a list of strings.
 *
 * ── Keeping it honest ───────────────────────────────────────────────────────────────
 * `SettingsPage` sorts its own menus by these lists, so this file is authoritative for
 * order by construction rather than by anyone remembering. An entry present in the menus
 * but missing here sorts LAST rather than vanishing, which is visible in the UI instead of
 * silent — the same stance `categoryOrder.ts` takes for an unmapped doc category.
 */

/** Settings → Personal, top to bottom. */
export const PERSONAL_NAV_ORDER: readonly string[] = [
  'Profile',
  // VRG-490 — right after Profile: Profile is who you are, Security is how
  // you prove it's you (password, two-factor), the same adjacency logic
  // Profile's own "Login" section header used before D7 moved password here.
  'Security',
  'Appearance',
  'Notifications',
  'Controls',
  'Activity',
  'My requests',
  'Refer & earn',
]

/** Settings → Company, top to bottom. */
export const COMPANY_NAV_ORDER: readonly string[] = [
  'General',
  'Installation',
  'Users',
  'Roles & permissions',
  'Features',
  'Billing',
  'Support',
  'Address book',
  'Integrations',
  'Fields',
  'Activity log',
]

/**
 * Sort by a label order, putting anything unlisted last while preserving its relative
 * position. Stable, so two unlisted entries keep the order they were declared in.
 */
export const byNavOrder = <T extends {label: string}>(
  items: readonly T[],
  order: readonly string[],
): T[] => {
  const rank = (label: string) => {
    const i = order.indexOf(label)
    return i === -1 ? Number.MAX_SAFE_INTEGER : i
  }
  return [...items].sort((a, b) => rank(a.label) - rank(b.label))
}
